import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { UpcomingEvents } from "@/components/hoops/UpcomingEvents";
import softballLogo from "@/assets/softball-logo.png";
import softballTournaments from "@/assets/softball-tournaments.jpg";
import softballRecruiting from "@/assets/softball-recruiting.jpg";
import softballShowcase from "@/assets/softball-showcase.jpg";
import softballCamps from "@/assets/softball-camps.jpg";
import softballGallery1 from "@/assets/softball-gallery-1.jpg";
import softballGallery2 from "@/assets/softball-gallery-2.jpg";
import softballGallery3 from "@/assets/softball-gallery-3.jpg";
import softballGallery4 from "@/assets/softball-gallery-4.jpg";
import softballGallery5 from "@/assets/softball-gallery-5.jpg";
import softballGallery6 from "@/assets/softball-gallery-6.jpg";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Target,
  Users,
  Trophy,
  Star,
  ArrowRight,
  CheckCircle,
  Calendar,
  MapPin,
  Award,
  TrendingUp,
  Eye,
  Clock,
  Globe,
  Zap,
  Shield,
  Heart
} from "lucide-react";

export default function Softball() {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      slidesToScroll: 1,
      breakpoints: {
        '(min-width: 768px)': { 
          slidesToScroll: 1 
        }
      }
    },
    [Autoplay({ delay: 4000, stopOnInteraction: true })]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  const galleryImages = [
    { src: softballGallery1, alt: "Softball player batting" },
    { src: softballGallery2, alt: "Softball player hitting" },
    { src: softballGallery3, alt: "Softball team huddle" },
    { src: softballGallery4, alt: "Softball player swinging" },
    { src: softballGallery5, alt: "Softball player at bat" },
    { src: softballGallery6, alt: "Softball team meeting" },
  ];

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi]
  );
  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      {/* Hero Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-br from-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://cdn.builder.io/api/v1/image/assets%2F7b5b7573f41a45539311717edd1f85db%2Ff9d47f097de84306899e887dc641b7f8?format=webp&width=1200"
            alt="Softball action"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <Badge className="bg-secondary text-black px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-black tracking-wider mb-6 sm:mb-8">
              ELITE SOFTBALL RECRUITING
            </Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-black mb-4 sm:mb-6 md:mb-8 leading-tight">
              RECRUITLOOK
              <span className="block text-secondary">SOFTBALL</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed px-4">
              Comprehensive suite of services designed to enhance the recruiting prospects of softball athletes 
              through elite tournaments, showcases, and direct college coach exposure.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            <div className="text-center">
              <div className="text-xl sm:text-2xl lg:text-3xl font-black text-secondary mb-2">For Coaches</div>
              <div className="text-xs sm:text-sm font-semibold">Where coaches find the talent they need.</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl lg:text-3xl font-black text-secondary mb-2">Year-Round</div>
              <div className="text-xs sm:text-sm font-semibold">Events</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl lg:text-3xl font-black text-secondary mb-2">Hands-On</div>
              <div className="text-xs sm:text-sm font-semibold">Instruction</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl lg:text-3xl font-black text-secondary mb-2">For Teams</div>
              <div className="text-xs sm:text-sm font-semibold">Where the best teams come to compete and get seen.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why RecruitLook Softball */}
      <section className="pt-6 sm:pt-8 pb-8 sm:pb-12 bg-gradient-to-br from-gray-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <div>
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-6">
                <img 
                  src={softballLogo} 
                  alt="RecruitLook Softball Logo" 
                  className="w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 mx-auto sm:mx-0"
                />
                <div className="text-center sm:text-left">
                  <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-slate-900">
                    WHY RECRUITLOOK
                    <span className="block text-secondary">SOFTBALL</span>
                  </h2>
                </div>
              </div>
              <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-gray-600 leading-relaxed">
                <p>
                  RecruitLook Softball Camps, Showcases, and Tournaments are built to give athletes the exposure, instruction, and competition needed to reach the next level. Our college camps bring players and college coaches together in an interactive setting, offering skill development, position-specific training, and live evaluations. With hands-on instruction and direct feedback, athletes gain insight into what it takes to succeed at the collegiate level while showcasing their abilities to decision-makers.
                </p>
              </div>
            </div>
            <div className="relative">
              <Badge className="bg-secondary text-black px-3 sm:px-4 py-1.5 sm:py-2 font-black mb-4 sm:mb-6 text-xs sm:text-sm">
                WHY RECRUITLOOK SOFTBALL
              </Badge>
              <AspectRatio ratio={16 / 9}>
                <iframe
                  className="w-full h-full rounded-xl sm:rounded-2xl"
                  src="https://www.youtube.com/embed/NhH2Mwn7yIM"
                  title="RecruitLook Softball"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </AspectRatio>
            </div>
          </div>

          {/* Softball Gallery Carousel */}
          <div className="mt-12 sm:mt-16 relative max-w-6xl mx-auto">
            <div className="embla overflow-hidden rounded-xl sm:rounded-2xl" ref={emblaRef}>
              <div className="embla__container flex">
                {galleryImages.map((image, index) => (
                  <div key={index} className="embla__slide flex-[0_0_100%] sm:flex-[0_0_50%] md:flex-[0_0_33.333%] min-w-0 px-1 sm:px-2">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-48 sm:h-56 md:h-64 lg:h-80 object-cover rounded-lg sm:rounded-xl md:rounded-2xl border-2 sm:border-4 border-primary"
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Navigation Arrows - Hidden on mobile */}
            <Button
              onClick={scrollPrev}
              variant="outline"
              size="icon"
              className="hidden sm:flex absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border-primary text-primary hover:text-primary z-10"
            >
              <ChevronLeft className="h-4 w-4 md:h-6 md:w-6" />
            </Button>
            
            <Button
              onClick={scrollNext}
              variant="outline"
              size="icon"
              className="hidden sm:flex absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border-primary text-primary hover:text-primary z-10"
            >
              <ChevronRight className="h-4 w-4 md:h-6 md:w-6" />
            </Button>
            
            {/* Dots Navigation */}
            <div className="flex justify-center mt-4 sm:mt-6 gap-2">
              {galleryImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollTo(index)}
                  className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all ${
                    index === selectedIndex 
                      ? "bg-primary scale-110" 
                      : "bg-gray-300 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="pt-4 sm:pt-6 pb-16 sm:pb-20 md:pb-24 bg-white">
        <div className="w-full bg-secondary h-16 sm:h-20 md:h-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black leading-tight text-center">
              <span className="block text-slate-900">UPCOMING</span>
              <span className="block text-white">EVENTS</span>
            </h2>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center pt-4 sm:pt-6 mb-6 sm:mb-8">
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Join us at exclusive softball tournaments, showcases, and combines designed to maximize your recruiting exposure
            </p>
          </div>

          <UpcomingEvents sportSlug="softball" sportName="softball" />
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 sm:py-20 md:py-24 bg-white">
        <div className="w-full bg-secondary h-16 sm:h-20 md:h-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 text-center">
              OUR SOFTBALL <span className="block text-white">SERVICES</span>
            </h2>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-base sm:text-lg text-slate-800 max-w-3xl mx-auto text-center pt-4 sm:pt-6 mb-8 sm:mb-10">
            Everything you need to elevate your softball recruiting journey and connect with college programs
          </p>

          <div className="space-y-8 sm:space-y-10">
            {/* 1. Tournaments (left) + image (right) */}
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-stretch">
              <div className="bg-gradient-to-br from-slate-900 to-gray-900 text-white rounded-xl sm:rounded-2xl p-6 sm:p-8">
                <Trophy className="w-12 h-12 sm:w-16 sm:h-16 text-secondary mb-4 sm:mb-6" />
                <h3 className="text-xl sm:text-2xl font-black mb-3 sm:mb-4">TOURNAMENTS</h3>
                <p className="text-gray-300 text-sm sm:text-base">
                  Year-round elite competition events providing club teams with maximum recruiting exposure opportunities.
                </p>
              </div>
              <div className="rounded-xl sm:rounded-2xl overflow-hidden h-60 sm:h-80 md:h-[360px]">
                <img
                  src={softballTournaments}
                  alt="Tournament gameplay"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* 2. College Recruiting (right) + image (left) */}
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-stretch">
              <div className="rounded-xl sm:rounded-2xl overflow-hidden h-60 sm:h-80 md:h-[360px] order-1 lg:order-none">
                <img
                  src={softballRecruiting}
                  alt="Coach scouting players"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-gradient-to-br from-secondary to-yellow-600 text-black rounded-xl sm:rounded-2xl p-6 sm:p-8">
                <Eye className="w-12 h-12 sm:w-16 sm:h-16 text-slate-900 mb-4 sm:mb-6" />
                <h3 className="text-xl sm:text-2xl font-black mb-3 sm:mb-4">COLLEGE RECRUITING</h3>
                <p className="text-slate-800 text-sm sm:text-base">
                  Strategic event design that positions recruits to be seen by the right college coaches at the right time.
                </p>
              </div>
            </div>

            {/* 3. College Showcases (left) + image (right) */}
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-stretch">
              <div className="bg-gradient-to-br from-slate-900 to-gray-900 text-white rounded-xl sm:rounded-2xl p-6 sm:p-8">
                <Star className="w-12 h-12 sm:w-16 sm:h-16 text-secondary mb-4 sm:mb-6" />
                <h3 className="text-xl sm:text-2xl font-black mb-3 sm:mb-4">COLLEGE SHOWCASES</h3>
                <p className="text-gray-300 text-sm sm:text-base">
                  Pairing performance with visibility. Showcase games feature college coaches actively coaching players in the dugouts and selecting the post-game MVPs for each team.
                </p>
              </div>
              <div className="rounded-xl sm:rounded-2xl overflow-hidden h-60 sm:h-80 md:h-[360px]">
                <img
                  src={softballShowcase}
                  alt="Showcase action"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* 4. College Camps (right) + image (left) */}
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-stretch">
              <div className="rounded-xl sm:rounded-2xl overflow-hidden h-60 sm:h-80 md:h-[360px] order-1 lg:order-none">
                <img
                  src={softballCamps}
                  alt="Instructional camp"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-gradient-to-br from-secondary to-yellow-600 text-black rounded-xl sm:rounded-2xl p-6 sm:p-8">
                <Shield className="w-12 h-12 sm:w-16 sm:h-16 text-slate-900 mb-4 sm:mb-6" />
                <h3 className="text-xl sm:text-2xl font-black mb-3 sm:mb-4">COLLEGE CAMPS</h3>
                <p className="text-slate-800 text-sm sm:text-base">
                  College Camp is designed to give athletes maximum exposure to college coaches while competing in a highly instructional environment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Benefits of RecruitLook Softball */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-6">
              BENEFITS OF
              <span className="block text-secondary">RECRUITLOOK SOFTBALL</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We provide unmatched opportunities for softball athletes to showcase their talents and connect with college programs
            </p>
            <p className="text-gray-300 max-w-4xl mx-auto mt-4">
              Our showcases and tournaments provide premier platforms for players to compete against top talent while being seen by a wide network of college recruiters. Each event is structured to maximize recruiting opportunities, from high-level competition on the field to organized recruiting formats that highlight player performance. Whether you're looking to sharpen your skills, get noticed by coaches, or take that next step in the recruiting process, RecruitLook Softball delivers the pathway to your future.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <Users className="w-16 h-16 text-secondary mx-auto mb-6" />
              <h3 className="text-2xl font-black mb-4">MASSIVE EXPOSURE</h3>
              <p className="text-gray-300">
                Hundreds of college coaches in our database, providing pathways for evaluation, outreach, and real recruiting traction.
              </p>
            </div>

            <div className="text-center">
              <Trophy className="w-16 h-16 text-secondary mx-auto mb-6" />
              <h3 className="text-2xl font-black mb-4">ELITE COMPETITION</h3>
              <p className="text-gray-300">
                Top teams from all over the country participate, ensuring high-level competition and meaningful evaluations.
              </p>
            </div>

            <div className="text-center">
              <Target className="w-16 h-16 text-secondary mx-auto mb-6" />
              <h3 className="text-2xl font-black mb-4">ON-SITE SCHOLARSHIPS</h3>
              <p className="text-gray-300">
                Scholarships are often extended directly at our events, creating immediate recruiting opportunities.
              </p>
            </div>

            <div className="text-center">
              <Clock className="w-16 h-16 text-secondary mx-auto mb-6" />
              <h3 className="text-2xl font-black mb-4">YEAR-ROUND EVENTS</h3>
              <p className="text-gray-300">
                Continuous tournament schedule ensures multiple opportunities throughout the recruiting calendar.
              </p>
            </div>

            <div className="text-center">
              <Eye className="w-16 h-16 text-secondary mx-auto mb-6" />
              <h3 className="text-2xl font-black mb-4">STRATEGIC VISIBILITY</h3>
              <p className="text-gray-300">
                Events are designed specifically to position recruits for maximum visibility to college coaches.
              </p>
            </div>

            <div className="text-center">
              <Heart className="w-16 h-16 text-secondary mx-auto mb-6" />
              <h3 className="text-2xl font-black mb-4">PARTNERSHIP QUALITY</h3>
              <p className="text-gray-300">
                Official partnership with Decker ensures top-quality sportswear and equipment for all events.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-24 bg-gradient-to-br from-gray-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-6 text-slate-900">
              OFFICIAL
              <span className="block text-secondary">PARTNERSHIPS</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We partner with industry leaders to provide the best experience for our athletes
            </p>
          </div>

          <div className="bg-white rounded-2xl p-12 shadow-lg text-center">
            <div className="flex items-center justify-center gap-8 mb-8">
              <img src="https://cdn.builder.io/api/v1/image/assets%2F7b5b7573f41a45539311717edd1f85db%2F3bc35738a97547b7a6a0381602498d65?format=webp&width=400" alt="Decker" className="h-16 md:h-20 object-contain" />
              <div className="text-4xl md:text-5xl text-gray-300">×</div>
              <img src="https://cdn.builder.io/api/v1/image/assets%2F7b5b7573f41a45539311717edd1f85db%2F6ac023f8aa6448d09e6e8cf9fc96d41b?format=webp&width=400" alt="RecruitLook Softball" className="h-16 md:h-20 object-contain" />
            </div>
            <h3 className="text-3xl font-black mb-4 text-slate-900">DECKER PARTNERSHIP</h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              RecruitLook Softball partners with Decker to provide quality sportswear and equipment 
              for our events and athletes, ensuring professional standards at every tournament.
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-black mb-8 text-slate-900">
            READY TO ELEVATE
            <span className="block text-secondary">YOUR GAME?</span>
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
            Join hundreds of softball athletes who have advanced their recruiting prospects through RecruitLook events.
          </p>
          

          <div className="flex items-center justify-center space-x-8 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-secondary" />
              <span className="font-semibold">College Coach Guaranteed</span>
            </div>
            <div className="flex items-center space-x-2">
              <Trophy className="w-5 h-5 text-secondary" />
              <span className="font-semibold">Elite Competition</span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="w-5 h-5 text-secondary" />
              <span className="font-semibold">Proven Results</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}